interface SanitizeDecimalOptions {
    /** Maximum digits after the decimal point. Omit for no limit. */
    maxDecimals?: number;
    /** Maximum digits before the decimal point. Omit for no limit. */
    maxIntegerDigits?: number;
}

/**
 * Strip non-numeric characters and enforce at most one decimal point in a
 * raw input string. Used by amount inputs so a paste of `"1.2.3abc"` lands
 * as `"1.23"`. Does NOT validate range, leading zeros, or empty strings —
 * those are layout/UX concerns.
 *
 * - Normalizes commas to dots (Nordic / European mobile keyboards expose
 *   only `,` on the numeric pad, so `1,2` must be accepted as `1.2`).
 * - Strips anything except `0–9` and `.`.
 * - Keeps only the first decimal point; subsequent dots are dropped.
 * - Truncates the integer part to `maxIntegerDigits` digits when set.
 * - Truncates the fractional part to `maxDecimals` digits when set.
 */
export function sanitizeDecimal(raw: string, { maxDecimals, maxIntegerDigits }: SanitizeDecimalOptions = {}): string {
    const normalized = raw.replace(/,/g, ".");
    const stripped = normalized.replace(/[^0-9.]/g, "");
    const firstDot = stripped.indexOf(".");
    if (firstDot === -1) {
        return maxIntegerDigits !== undefined ? stripped.slice(0, maxIntegerDigits) : stripped;
    }
    const intPart = stripped.slice(0, firstDot);
    const intTruncated = maxIntegerDigits !== undefined ? intPart.slice(0, maxIntegerDigits) : intPart;
    const fracPart = stripped.slice(firstDot + 1).replace(/\./g, "");
    const truncated = maxDecimals !== undefined ? fracPart.slice(0, maxDecimals) : fracPart;
    return `${intTruncated}.${truncated}`;
}
